import { useState, type ChangeEvent, type ReactNode } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useLanguage } from '../../../../context/LanguageContext';

interface PurchasePeriodQuickSelectProps {
    onApply: (value: string) => void;
    // Rendered between the "Je sélectionne les" prefix and the months field, e.g. the
    // Actifs/Inactifs select in PurchaseActivityRow, so the whole thing reads as one sentence.
    beforeMonths?: ReactNode;
}

const toIsoDate = (date: Date): string => date.toISOString().slice(0, 10);

// Between-editor date values are formatted as "min|max" (see BetweenEditor).
export const lastMonthsRange = (months: number): string => {
    const to = new Date();
    const from = new Date(to);
    from.setMonth(from.getMonth() - months);
    return `${toIsoDate(from)}|${toIsoDate(to)}`;
};

// Reverse of lastMonthsRange, used to restore the months field when re-opening a row whose rule was
// already applied (e.g. loading a saved query), since the UI only stores the resulting date range.
export const parseMonthsFromRange = (value: unknown): number | null => {
    if (typeof value !== 'string' || !value.includes('|')) return null;
    const [minStr, maxStr] = value.split('|');
    const min = new Date(minStr);
    const max = new Date(maxStr);
    if (Number.isNaN(min.getTime()) || Number.isNaN(max.getTime())) return null;
    const months = Math.round((max.getFullYear() - min.getFullYear()) * 12 + (max.getMonth() - min.getMonth()));
    return months > 0 ? months : null;
};

function PurchasePeriodQuickSelect({ onApply, beforeMonths }: PurchasePeriodQuickSelectProps) {
    const { t } = useLanguage();
    const [months, setMonths] = useState(12);

    const handleMonthsChange = (event: ChangeEvent<HTMLInputElement>) => {
        const parsed = parseInt(event.target.value, 10);
        setMonths(Number.isNaN(parsed) ? 1 : Math.max(1, parsed));
    };

    const handleApply = () => onApply(lastMonthsRange(months));

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
            <Typography variant="caption" sx={{ color: '#495057' }}>
                {t('pages.clients.queryBuilder.simple.quickPeriod.prefix')}
            </Typography>
            {beforeMonths}
            <TextField
                type="number"
                size="small"
                value={months}
                onChange={handleMonthsChange}
                inputProps={{ min: 1 }}
                sx={{ width: 72 }}
            />
            <Typography variant="caption" sx={{ color: '#495057' }}>
                {t('pages.clients.queryBuilder.simple.quickPeriod.suffix')}
            </Typography>
            <Button size="small" variant="outlined" onClick={handleApply}>
                {t('pages.clients.queryBuilder.simple.quickPeriod.apply')}
            </Button>
        </Box>
    );
}

export default PurchasePeriodQuickSelect;
